import React, { useState, useEffect, useRef, useId } from 'react';
import './ModalWrapper.css';

/**
 * Accessible dialog wrapper.
 *
 * WCAG-relevant behavior built in:
 *   - role="dialog" + aria-modal="true"
 *   - aria-labelledby (auto-derived from headerContent when ariaLabel is not provided)
 *   - Esc closes the dialog
 *   - Focus is moved into the dialog on open and returned to the previously focused
 *     element on close
 *   - Tab/Shift+Tab is trapped inside the dialog while open
 *   - The rest of the page is marked aria-hidden while the dialog is open
 *
 * Set `preventClose` to disable overlay-click, drag-to-close, and Esc — useful for
 * blocking confirms.
 */
const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ModalWrapper = ({
  isOpen,
  onClose,
  children,
  headerContent,
  footerContent,
  bottomButtonsPresent = false,
  dragThreshold = 100,
  dragStartArea = 50,
  customStyles = {},
  className = '',
  preventClose = false,
  ariaLabel,
}) => {
  const [dragStartY, setDragStartY] = useState(null);
  const [dragDelta, setDragDelta] = useState(0);
  const modalRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const titleId = useId();

  // Lock background scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Save previously focused element + move focus into the dialog on open;
  // restore focus on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    previouslyFocusedRef.current = document.activeElement;

    // Wait one tick so children render before focusing.
    const t = setTimeout(() => {
      if (!modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      const target = focusable[0] || modalRef.current;
      target.focus();
    }, 0);

    return () => {
      clearTimeout(t);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [isOpen]);

  // Esc-to-close + focus trap
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !preventClose) {
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = Array.from(
        modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);

      if (focusable.length === 0) {
        // Nothing focusable inside — keep focus on the dialog itself
        e.preventDefault();
        modalRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, preventClose, onClose]);

  // Hide the rest of the app from assistive tech while modal is open
  useEffect(() => {
    if (!isOpen) return undefined;
    const root = document.getElementById('root');
    const main = document.getElementById('main');
    const targets = [root, main].filter(Boolean);
    const previousValues = targets.map((el) => el.getAttribute('aria-hidden'));
    targets.forEach((el) => {
      // Only hide siblings — but the modal lives inside #root, so we can't aria-hide
      // #root itself or we hide ourselves. Instead hide #main (the routed content)
      // which is the realistic "other content" the modal should obscure.
      if (el.id === 'main') el.setAttribute('aria-hidden', 'true');
    });
    return () => {
      targets.forEach((el, i) => {
        if (el.id !== 'main') return;
        if (previousValues[i] === null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', previousValues[i]);
      });
    };
  }, [isOpen]);

  const handleTouchStart = (e) => {
    if (preventClose) return;
    if (e.touches.length === 1 && modalRef.current) {
      const touchY = e.touches[0].clientY;
      const modalRect = modalRef.current.getBoundingClientRect();
      if (touchY - modalRect.top <= dragStartArea) {
        setDragStartY(touchY);
      }
    }
  };

  const handleTouchMove = (e) => {
    if (dragStartY !== null) {
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - dragStartY;
      if (deltaY > 0) {
        setDragDelta(deltaY);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!preventClose && dragDelta > dragThreshold) {
      onClose();
    } else {
      setDragDelta(0);
    }
    setDragStartY(null);
  };

  if (!isOpen) return null;

  // Derive label/labelledby. If ariaLabel is provided, prefer that. Otherwise, if
  // headerContent renders, point aria-labelledby at the header wrapper.
  const labelProps = ariaLabel
    ? { 'aria-label': ariaLabel }
    : headerContent
      ? { 'aria-labelledby': titleId }
      : { 'aria-label': 'Dialog' };

  return (
    <div
      className={`modal-wrapper-overlay ${className}`}
      onClick={preventClose ? undefined : onClose}
    >
      <div
        className={`modal-wrapper-container ${className}`}
        style={{
          transform: `translateY(${dragDelta}px)`,
          paddingBottom: bottomButtonsPresent
            ? 'calc(20px + env(safe-area-inset-bottom))'
            : '20px',
          ...customStyles.container,
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        tabIndex="-1"
        {...labelProps}
      >
        {headerContent && (
          <div id={titleId} className="modal-wrapper-header">
            {headerContent}
          </div>
        )}
        <div className="modal-wrapper-content" style={customStyles.content}>
          {children}
        </div>
        {footerContent && (
          <div className="modal-wrapper-footer">
            {footerContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalWrapper;
